import { useEffect, useState } from "react";
import { api, API_BASE, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ImagePlus, Loader2, Upload, X, Heart } from "lucide-react";

/**
 * Phase 25 — Public Gallery page + Customer Photo Upload.
 *
 * Route: /gallery
 * Customers see the moderated gallery in a magazine-style masonry grid +
 * a one-tap upload form that drops their photo into the moderation queue.
 */
export default function GalleryPage() {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/gallery");
      setPhotos(data.items || []);
    } catch (e) { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    document.title = "Gallery — Snaps from our kitchen + yours";
    refresh();
  }, []);

  return (
    <div className="bg-[#FBF7F0] min-h-screen" data-testid="gallery-page">
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.22em] font-medium text-stone-500 inline-flex items-center gap-2">
          <Camera className="w-3.5 h-3.5" /> The gallery
        </p>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[0.95] max-w-3xl">
          Snaps from our kitchen <em className="font-normal italic text-stone-500">— and yours</em>.
        </h1>
        <p className="mt-5 text-stone-600 max-w-2xl">
          The plates we're proud of, the regulars we love, and the photos
          you've taken at the table. Tap the camera to share your shot — best
          ones get featured here and re-shared on our Instagram.
        </p>
        <button
          onClick={() => setShowUpload(true)}
          data-testid="gallery-upload-open"
          className="mt-7 inline-flex items-center gap-2 bg-stone-900 text-white text-sm font-medium px-5 py-3 rounded-full hover:-translate-y-0.5 transition"
        >
          <ImagePlus className="w-4 h-4" /> Share your photo
        </button>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-stone-400" /></div>
        ) : photos.length === 0 ? (
          <div className="text-center py-16 text-stone-500 italic" data-testid="gallery-empty">
            No photos in the gallery yet — be the first to share!
          </div>
        ) : (
          <div className="columns-2 md:columns-3 lg:columns-4 gap-3 space-y-3" data-testid="gallery-grid">
            {photos.map((p) => (
              <figure
                key={p.id}
                data-testid={`gallery-photo-${p.id}`}
                className="break-inside-avoid relative rounded-2xl overflow-hidden bg-white border border-stone-100 group"
              >
                <img
                  src={p.image_data_url || p.public_url}
                  alt={p.caption || "Gallery"}
                  className="w-full h-auto object-cover transition-transform group-hover:scale-[1.02] select-none pointer-events-auto"
                  loading="lazy"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />
                {(p.caption || p.customer_name) && (
                  <figcaption className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 via-black/30 to-transparent text-white">
                    {p.caption && <div className="text-sm leading-tight">{p.caption}</div>}
                    {p.customer_name && <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">— {p.customer_name}</div>}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </section>

      {showUpload && <UploadModal onClose={() => { setShowUpload(false); refresh(); }} />}
    </div>
  );
}

function UploadModal({ onClose }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [caption, setCaption] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 4 * 1024 * 1024) { toast.error("Photo must be under 4 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => { setFile(f); setPreview(String(reader.result || "")); };
    reader.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!preview) { toast.error("Pick a photo first"); return; }
    setSending(true);
    try {
      await api.post("/gallery/customer-upload", {
        image: preview, caption, customer_name: name, customer_email: email,
      });
      toast.success("Thanks! Your photo is in moderation.");
      onClose();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally { setSending(false); }
  };

  return (
    <div
      data-testid="gallery-upload-modal"
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="font-serif text-2xl text-stone-900">Share your photo</h2>
            <p className="text-xs text-stone-500 mt-1">We may feature you on the site or our Instagram.</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1.5 rounded-full hover:bg-stone-100">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <label className="block mt-4">
          <span className="text-xs uppercase tracking-widest text-stone-500">Photo</span>
          <input
            data-testid="gallery-upload-file"
            type="file" accept="image/*" capture="environment"
            onChange={pick}
            className="mt-1 block w-full text-sm text-stone-600 file:mr-3 file:rounded-full file:border-0 file:bg-stone-900 file:text-white file:text-xs file:font-medium file:py-2 file:px-4 file:cursor-pointer"
          />
        </label>
        {preview && (
          <img
            src={preview}
            alt="Preview"
            className="mt-3 rounded-xl max-h-56 mx-auto"
            data-testid="gallery-upload-preview"
          />
        )}
        <label className="block mt-3">
          <span className="text-xs uppercase tracking-widest text-stone-500">Caption</span>
          <input
            data-testid="gallery-upload-caption"
            value={caption} onChange={(e) => setCaption(e.target.value)}
            maxLength={240}
            className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
            placeholder="Best ravioli of my life"
          />
        </label>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <label>
            <span className="text-xs uppercase tracking-widest text-stone-500">First name</span>
            <input
              value={name} onChange={(e) => setName(e.target.value)}
              data-testid="gallery-upload-name"
              maxLength={80}
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
            />
          </label>
          <label>
            <span className="text-xs uppercase tracking-widest text-stone-500">Email <span className="opacity-50">(opt.)</span></span>
            <input
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="gallery-upload-email"
              type="email"
              className="mt-1 w-full rounded-lg border border-stone-200 px-3 py-2 text-sm"
            />
          </label>
        </div>
        <button
          type="submit" disabled={sending || !preview}
          data-testid="gallery-upload-submit"
          className="w-full mt-5 inline-flex items-center justify-center gap-2 bg-stone-900 text-white rounded-full py-3 text-sm font-medium disabled:opacity-60"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          Share photo
        </button>
        <p className="text-[10px] text-stone-400 text-center mt-2">
          <Heart className="w-3 h-3 inline -mt-0.5" /> By submitting, you let us feature your photo on our site and socials.
        </p>
      </form>
    </div>
  );
}
